import React from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { SERVICES } from "../lib/brand";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} />;
};

export const ServicesSection = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-slate-50 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#00A3E0]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-600">
              Our Services
            </span>
          </div>
          <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
            Built for execution, not just advice.
          </h2>
          <p className="mt-5 text-base text-slate-600 sm:text-lg">
            Six core practices that combine into a single end-to-end engagement —
            from opportunity to operating asset.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              className="flip-card h-[320px]"
              data-testid={`service-card-${i}`}
            >
              <div className="flip-card-inner h-full w-full">
                {/* Front */}
                <div className="flip-card-face flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#00A3E0]/10 text-[#00A3E0]">
                      <Icon name={s.icon} className="h-6 w-6" />
                    </div>
                    <span className="text-[10px] font-semibold tracking-[0.28em] text-slate-400">
                      0{i + 1}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold leading-tight text-slate-900">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">
                      {s.description}
                    </p>
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                    <span className="text-xs font-semibold text-slate-400">
                      Hover to expand
                    </span>
                    <Icons.MoveUpRight className="h-4 w-4 text-[#00A3E0]" />
                  </div>
                </div>

                {/* Back */}
                <div className="flip-card-face flip-card-back flex h-full flex-col justify-between rounded-2xl bg-[#0F172A] p-7 text-white shadow-xl">
                  <div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#00A3E0]/20 text-[#00A3E0]">
                      <Icon name={s.icon} className="h-6 w-6" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-bold leading-tight">
                      {s.title}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2 text-sm text-white/85"
                      >
                        <Icons.ChevronRight className="mt-0.5 h-4 w-4 flex-none text-[#00A3E0]" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="border-t border-white/10 pt-4">
                    <a
                      href="/submit-project"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A3E0]"
                    >
                      Start an engagement <Icons.ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
